import { htmlZuKurztext, htmlZuText } from '../../domain/htmlText';
import { frac, fromNumber, type Fraction } from '../../domain/points';
import { woerterIn } from '../../domain/woerter';
import type { ExamBundle } from '../../persistence/bundle';
import { toRelayDate } from '../../persistence/relay';
import type {
  ActorRow, CandidateRow, ExamActorRow, ExamAnswerRow, ExamCandidateRow, ExamExaminerRow,
  ExamItemContainerRow, ExamItemPredefinedCommentPhraseListRow, ExamItemRow, ExamPartRow,
  ExamQuestionRow, ExaminerRow, MediaRow, PredefinedCommentPhraseRow, ClassicKennungRow,
} from '../../persistence/rows';
import { kennungGlobalerAntwort, zeilenAlsRoh, type GlobaleAntwortzeile } from './kennung';
import type { ClassicAnswer, ClassicOsce, ClassicPhrase, ClassicTask } from './typen';

/**
 * Aus einer Classic-Prüfung ein `ExamBundle` machen — **rein**, ohne Netz und ohne Ablage.
 *
 * Das ist das Herzstück des Kompatibilitätsmodus: Classic hat vier Ebenen, Relay sechs, und
 * mehrere Felder haben auf der Gegenseite keinen Platz. Was dabei verloren geht, steht in
 * `docs/protokoll-classic.md` §5 — hier steht an jeder Stelle, **was** verworfen wird und warum,
 * damit niemand später einen stillen Verlust für einen Fehler hält.
 *
 * ```
 * Classic   Osce ─────────── Station ──────── Task ───────────────── Answer
 * Relay   Exam ─ ExamPart ─ ExamItemContainer ─ ExamItem ─ ExamQuestion ─ ExamAnswer
 * ```
 *
 * `ExamPart` und `ExamQuestion` werden erfunden — einer je Prüfung, eine je Aufgabe.
 */

/** Der Download bricht ab, statt zu raten. Die Meldung benennt die Stelle. */
export class ClassicPruefungUnlesbar extends Error {
  constructor(
    message: string,
    readonly stelle?: { station?: string; aufgabe?: string },
  ) {
    super(message);
    this.name = 'ClassicPruefungUnlesbar';
  }
}

/**
 * Jede Kennung bekommt einen Vorsatz.
 *
 * Nicht Kosmetik: Relay-Kennungen sind UUIDs, Classic-Kennungen fortlaufende Zahlen. Ohne Vorsatz
 * könnte dieselbe Zeichenkette in beiden Welten etwas anderes bedeuten, sobald ein Gerät
 * nacheinander an beiden Servern hängt. Ableitbar müssen sie sein, damit ein zweiter Download
 * dieselben Zeilen trifft und nicht alles verdoppelt.
 */
export const kennung = {
  exam: (id: number | string) => 'classic:osce:' + String(id),
  part: (id: number | string) => 'classic:part:' + String(id),
  station: (id: number | string) => 'classic:station:' + String(id),
  item: (id: number | string) => 'classic:task:' + String(id),
  question: (id: number | string) => 'classic:question:' + String(id),
  answer: (id: number | string) => 'classic:answer:' + String(id),
  examiner: (id: number | string) => 'classic:examiner:' + String(id),
  actor: (id: number | string) => 'classic:actor:' + String(id),
  candidate: (id: number | string) => 'classic:candidate:' + String(id),
  media: (id: number | string) => 'classic:media:' + String(id),
  phraseList: (id: number | string) => 'classic:phraselist:' + String(id),
  phrase: (id: number | string) => 'classic:phrase:' + String(id),
  examExaminer: (o: number | string, e: number | string) => 'classic:examexaminer:' + String(o) + ':' + String(e),
  examActor: (o: number | string, a: number | string) => 'classic:examactor:' + String(o) + ':' + String(a),
  examCandidate: (o: number | string, c: number | string) => 'classic:examcandidate:' + String(o) + ':' + String(c),
} as const;

/**
 * Punkte: erst der Bruch, dann die Fliesskommazahl.
 *
 * Classic führt beides. Der Importer des Servers schreibt den Bruch in Hundertsteln
 * (`punkte·faktor·100 / Σfaktor·100`), ältere Zeilen tragen die Vorgabe `-1`, und aus einer
 * Berechnung mit gebrochenem Faktor kann auch etwas Nichtganzzahliges dastehen — `frac` rechnete
 * damit still falsch. Deshalb wird der Bruch nur genommen, wenn er wirklich einer ist.
 *
 * Der Rückfall ist `fromNumber`: der **einfachste** Bruch mit Nenner bis 1000 (Entscheidung des
 * Nutzers vom 2026-09-11). `0,5` wird `1/2`, `0,33` wird `33/100`.
 */
export function bruchAus(maxPoints?: number, num?: number, den?: number): Fraction {
  if (
    typeof num === 'number' && typeof den === 'number' &&
    Number.isInteger(num) && Number.isInteger(den) && den > 0 && num >= 0
  ) {
    return frac(num, den);
  }
  if (typeof maxPoints === 'number' && Number.isFinite(maxPoints) && maxPoints >= 0) {
    return fromNumber(maxPoints);
  }
  return frac(0, 1);
}

export type Bewertungsart =
  | { art: 'kriterien'; stufen: string[] }
  | { art: 'checkliste' }
  | { art: 'global' };

/**
 * Die Bewertungsart **einer Antwortzeile**, in der Reihenfolge, in der der Classic-Client prüft
 * (`docs/protokoll-classic.md` §3).
 *
 * ⚠️ `singleChoice` heisst „je Zeile ein Häkchen", nicht „Einfachauswahl" — Classic kennt keine.
 *
 * Zwei Formen werden **abgelehnt statt geraten**: `ims.osce.custom1|2|3` trägt zwar eine im
 * Server fest verdrahtete Stufenzahl, aber die Beschriftungen standen nur im Sprachkatalog der
 * alten App und kommen über keine Leitung; `##ERROR##` schreibt der Importer, wenn er den Modus
 * selbst nicht erkannt hat. Beides anzuzeigen hiesse, dem Prüfer eine Skala zu erfinden.
 */
export function bewertungsartVon(antwort: ClassicAnswer, stelle?: { station?: string; aufgabe?: string }): Bewertungsart {
  const modus = antwort.evaluationmode ?? '';

  if (modus === '##ERROR##') {
    throw new ClassicPruefungUnlesbar(
      'Der Classic-Server liefert für eine Antwort den Bewertungsmodus „##ERROR##" — sein eigener ' +
        'Importer hat die Skala nicht erkannt. Die Prüfung müsste auf dem Server erneut eingelesen werden.',
      stelle,
    );
  }
  if (modus.startsWith('ims.osce.custom')) {
    throw new ClassicPruefungUnlesbar(
      'Die Antwort benutzt die Skala „' + modus + '". Deren Stufenbeschriftungen stehen in keiner ' +
        'Datenquelle des Servers — sie lagen nur im Sprachkatalog der alten App. Diese Prüfung ' +
        'lässt sich nicht ohne erfundene Beschriftungen anzeigen.',
      stelle,
    );
  }

  if (modus.includes('#')) return { art: 'kriterien', stufen: modus.split('#') };
  if (antwort.singleChoice === true) return { art: 'checkliste' };
  return { art: 'global' };
}

/**
 * Aus dem HTML einer Station, Aufgabe oder Antwort lesbaren Klartext machen.
 *
 * Die Arbeit macht `domain/htmlText.ts` — sie ist für beide Gleise dieselbe. Hier bleibt nur
 * die **Eigenheit des Classic-Servers**: der `javascript:`-Link mit den Prüferinstruktionen ist kein
 * Fliesstext. Sein Inhalt wird getrennt geholt (`pruefInstruktionen`) und bliebe sonst als
 * blosse Beschriftung „Instruktionen für Prüfer:innen" ohne die Instruktionen selbst übrig.
 */
const INSTRUKTIONSLINK = /<a\b[^>]*javascript:App\.showExaminerInstructions[^>]*>.*?<\/a>/gis;

export function alsText(html: string, mitAbsaetzen = false): string {
  return htmlZuText(html, { mitAbsaetzen, entferne: [INSTRUKTIONSLINK] });
}

/**
 * Das HTML eines Prüfungstexts zum **Anzeigen** (seit 2026-10-02, Vorgabe des Nutzers): ohne
 * den Instruktionslink, sonst unverändert — bereinigt wird erst beim Zeigen
 * (`domain/htmlSicher.ts`). Steht kein sichtbarer Text darin, bleibt es leer: 338 von 412
 * echten Antworten tragen nur `<p>&#xa0;</p><p>&#8203;</p>`.
 */
export function alsAnzeigeHtml(html: string): string {
  return alsText(html) === '' ? '' : html.replace(INSTRUKTIONSLINK, '').trim();
}

/**
 * Die Instruktionen für die Prüferin, versteckt in einem `javascript:`-Link.
 *
 * ⚠️ **Das ist echter Prüfungsinhalt an einer sehr unerwarteten Stelle.** Der Importer des
 * Classic-Servers legt sie als
 * `<a href="javascript:App.showExaminerInstructions('Titel','Inhalt')">` ab — Titel und Inhalt
 * stehen als **Argumente in der URL**. Wer das HTML nur zu Klartext strippt, wirft sie
 * vollständig weg und behält die blosse Beschriftung „Instruktionen für Prüfer:innen" übrig.
 * Am echten Server stecken so 30 Anleitungen, alle an Stationen, keine an Aufgaben
 * (2026-09-11 über alle 19 Prüfungen gezählt).
 *
 * Der Inhalt ist seinerseits HTML mit einfachen Anführungszeichen als Argumenttrenner. Ein
 * Apostroph im Text wäre nicht zu unterscheiden — deshalb wird vom **letzten** `')` her
 * gelesen, nicht vom ersten.
 */
export function pruefInstruktionen(html: string): string {
  const treffer = /javascript:App\.showExaminerInstructions\((.*)$/is.exec(html);
  if (!treffer) return '';
  const rest = treffer[1];
  const ende = rest.lastIndexOf("')");
  if (ende < 0) return '';
  const argumente = rest.slice(0, ende);
  const trenner = argumente.indexOf("','");
  if (trenner < 0) return '';
  // Der Inhalt beginnt nach dem Trenner; das führende `'` des ersten Arguments fällt weg.
  return alsText(argumente.slice(trenner + 3), true);
}

/** Dasselbe, aber gekürzt — für Kopfzeilen und Popups (`ExamItem.anchorCriterion`). */
export function alsKurztext(html: string, hoechstens = 120): string {
  return htmlZuKurztext(html, hoechstens, { entferne: [INSTRUKTIONSLINK] });
}

/**
 * Aus dem HTML einer Station das, was in `ExamItemContainer.vignette` stehen darf.
 *
 * ⚠️ **Die Spalte ist bei uns kein Freitext, sondern JSON.** `readExam.ts` liest sie mit
 * `JSON.parse(...) as Vignette`; rohes HTML dort lässt nicht nur diese Station, sondern die
 * **ganze Prüfung** beim Lesen scheitern (der Test „liest die drei Bewertungsarten" hat genau
 * das gezeigt). Classic kennt die Dreiteilung teaser/lead/task nicht — ein HTML-Blob lässt sich
 * nicht ehrlich in sie zerlegen.
 *
 * Deshalb: der Klartext füllt `teaser` und `lead`, und das **unveränderte HTML** reist als
 * zusätzlicher Schlüssel `html` mit. `readExam` liest ihn nicht und stolpert nicht darüber —
 * so geht nichts verloren, und Etappe 2 (die Anzeigefrage aus P15, mit den eingebetteten
 * Bildern und den `javascript:`-Links) kann ihn aufgreifen, ohne dass die Prüfung neu geladen
 * werden muss.
 */
export function alsVignette(html: string): string | null {
  if (html.trim() === '') return null;
  const instruktionen = pruefInstruktionen(html);
  // Mit Absätzen: 85 von 86 Stationstexten im echten Bestand sind mehrabsätzig. Zu einer
  // Zeile zusammengezogen sind sie für den Prüfer nicht mehr lesbar.
  const lead = alsText(html, true);
  if (lead === '' && instruktionen === '') return null;
  return JSON.stringify({
    teaser: alsKurztext(html),
    lead,
    task: '',
    instructions: instruktionen,
    // Ohne den Instruktionslink: die Instruktionen stehen in ihrem eigenen Block, und der Link
    // bliebe sonst als blosse Beschriftung im angezeigten Text stehen (2026-10-02).
    html: alsAnzeigeHtml(html),
  });
}

/** `yyyyMMddHHmmss` (Classic, 14 Stellen) → `yyyyMMddHHmmssSSS` (Relay, 17). */
export function alsRelayZeitstempel(lastUpdate: string | undefined, ersatz: Date): string {
  const ziffern = (lastUpdate ?? '').replace(/\D/g, '');
  if (ziffern.length >= 17) return ziffern.slice(0, 17);
  if (ziffern.length === 14) return ziffern + '000';
  // Weder das eine noch das andere: lieber die eigene Uhr als eine zurechtgebogene Zeichenkette,
  // die später lexikografisch mit echten Zeitstempeln verglichen wird.
  return toRelayDate(ersatz) + '000000000';
}

const alsDatum = (wert: string | undefined): string | null =>
  /^\d{8}$/.test(wert ?? '') ? (wert as string) : null;

/** Was der Download an Dateien noch holen muss. */
export interface MediumZumHolen {
  /** Die **rohe** Classic-Kennung — sie steht so in der URL `/rest/media/{id}`. */
  id: number;
  mime: string;
}

export interface Abbildung {
  bundle: ExamBundle;
  medien: MediumZumHolen[];
}

export function zuBundle(osce: ClassicOsce, jetzt: Date = new Date()): Abbildung {
  if (osce.id === undefined || osce.id === null) {
    // `GET /rest/osces/{id}` antwortet bei unbekannter Kennung mit Status 200 und `{}`.
    // Wer nur auf den Statuscode sieht, legt hier eine leere Prüfung an.
    throw new ClassicPruefungUnlesbar(
      'Der Server hat eine Prüfung ohne Kennung geliefert. Bei einer unbekannten Kennung ' +
        'antwortet Classic mit einem leeren Rumpf statt mit einem Fehler.',
    );
  }
  if (!Array.isArray(osce.stations)) {
    throw new ClassicPruefungUnlesbar('Die Prüfung „' + (osce.name ?? osce.id) + '" enthält keine Stationen.');
  }

  const examId = kennung.exam(osce.id);
  const partId = kennung.part(osce.id);
  const startDate = alsDatum(osce.startDate);

  const bundle: ExamBundle = {
    exam: {
      id: examId,
      name: osce.name ?? String(osce.id),
      // HTML, unverändert übernommen — die Anzeigefrage steht in P15 und gehört in Etappe 2.
      description: osce.description ?? null,
      examType: 'OSCE',
      // ⚠️ **Classic kennt keine Simulationspruefung.** Der Classic-Server hat kein solches Feld;
      // eine ueber dieses Gleis geholte Pruefung ist immer echt. Das ist die Wahrheit, keine
      // Luecke — s. `AUFSTIEG_5` in `persistence/schema.ts`.
      simulative: 'N' as const,
      // Classic kennt keine Hinweise an die Prüflinge (`AUFSTIEG_7`).
      candidateInstructions: null,
      // Classic liefert den Klartext in der Prüfungsliste; die Ablageform bildet der Download
      // (`features/sheets/pruefungsPasswort.ts`), nicht diese reine Abbildung.
      devicePassword: null,
      // Classic kennt keine Gesamtdauer, nur `Station.time`. Nichts zu erfinden hier.
      durationSeconds: null,
      startDate: startDate ?? toRelayDate(jetzt),
      // ⚠️ Erfunden. Classic führt zu einer Prüfung nur Tage, keine Uhrzeiten; Relay lässt
      // `startTime`/`endTime` nicht leer. Der ganze Tag ist die ehrlichste Näherung.
      startTime: '0000',
      endDate: alsDatum(osce.endDate),
      endTime: '2359',
      lastModifiedDate: alsRelayZeitstempel(osce.lastUpdate, jetzt),
      notice: null,
    },
    // Erfunden: `ExamItemContainer.examPartID` darf nicht leer sein, Classic kennt die Ebene nicht.
    parts: [{ id: partId, examID: examId, name: osce.name ?? '', orderNo: 0 } satisfies ExamPartRow],
    containers: [],
    items: [],
    questions: [],
    answers: [],
    examiners: [],
    examExaminers: [],
    actors: [],
    examActors: [],
    candidates: [],
    examCandidates: [],
    media: [],
    phraseLists: [],
    phrases: [],
    itemPhraseLists: [],
  };

  const medien: MediumZumHolen[] = [];

  /**
   * Prüflinge sammeln sich über alle Stationen. ⚠️ **Der Laufplan geht dabei verloren** —
   * Classic ordnet den Prüfling der einzelnen Station zu, mit eigener Reihenfolge und eigener
   * Laufnummer; Relay' `ExamCandidate` kennt nur „gehört zur Prüfung" (P2, Entscheidung des
   * Nutzers vom 2026-09-11: flach abbilden). Als Laufnummer gilt die der Station mit dem
   * kleinsten `sortOrder`, damit dieselbe Prüfung zweimal dieselbe Nummer ergibt und nicht
   * die einer zufälligen Station.
   */
  const prüflinge = new Map<number, { zeile: CandidateRow; rang: number; nummer: string | null }>();

  for (const station of osce.stations) {
    if (station.id === undefined) {
      throw new ClassicPruefungUnlesbar('Eine Station der Prüfung „' + (osce.name ?? osce.id) + '" hat keine Kennung.');
    }
    const containerId = kennung.station(station.id);
    const stationsName = station.name ?? String(station.id);

    bundle.containers.push({
      id: containerId,
      examPartID: partId,
      name: stationsName,
      type: 'STATION',
      durationSeconds: typeof station.time === 'number' ? station.time : null,
      orderNo: station.sortOrder ?? 0,
      // HTML mit eingebetteten Bildern — gesäubert für die Anzeige, im Original erhalten (P15).
      vignette: alsVignette(station.description ?? ''),
      comment: null,
    } satisfies ExamItemContainerRow);

    for (const medium of station.media ?? []) {
      if (medium.id === undefined) continue;
      bundle.media?.push({
        id: kennung.media(medium.id),
        name: medium.name ?? String(medium.id),
        mimeType: medium.mime ?? null,
        referenceType: 'EXAM_ITEM_CONTAINER',
        referenceIdentifier: containerId,
        referenceSubType: null,
        size: typeof medium.size === 'number' ? medium.size : -1,
        // ⚠️ `width` und `height` gehen verloren — `MediaRow` hat dafür keine Spalten (P6).
        orderNo: medium.sortorder ?? 0,
        description: medium.description ?? null,
      } satisfies MediaRow);
      medien.push({ id: medium.id, mime: medium.mime ?? '' });
    }

    for (const zuordnung of station.stationExaminees ?? []) {
      const prüfling = zuordnung.examinee;
      if (!prüfling || prüfling.id === undefined) continue;
      const rang = zuordnung.sortOrder ?? Number.MAX_SAFE_INTEGER;
      const vorhanden = prüflinge.get(prüfling.id);
      if (vorhanden && vorhanden.rang <= rang) continue;
      prüflinge.set(prüfling.id, {
        zeile: {
          id: kennung.candidate(prüfling.id),
          firstName: prüfling.firstname ?? '',
          lastName: prüfling.lastname ?? '',
          matriculationNumber: prüfling.matriculationnumber ?? null,
          // ⚠️ `rfid`, `groupname` und `surrogate` gehen verloren — `CandidateRow` hat dafür
          // keine Spalten (P11). `rfid` ist das, was am Stationseingang gescannt wird; im
          // Classic-Modus bleibt dafür die Matrikelnummer.
        },
        rang,
        nummer: zuordnung.osceExamineeNo ?? null,
      });
    }

    for (const aufgabe of station.tasks ?? []) {
      baueAufgabe(bundle, aufgabe, containerId, stationsName);
    }
  }

  for (const prüfer of osce.examiners ?? []) {
    if (prüfer.id === undefined) continue;
    bundle.examiners.push({
      id: kennung.examiner(prüfer.id),
      firstName: prüfer.firstname ?? '',
      lastName: prüfer.lastname ?? '',
      groupName: prüfer.groupname ?? '',
      key: prüfer.key ?? null,
      password: prüfer.password ?? '',
    } satisfies ExaminerRow);
    bundle.examExaminers.push({
      id: kennung.examExaminer(osce.id, prüfer.id),
      examID: examId,
      examinerID: kennung.examiner(prüfer.id),
    } satisfies ExamExaminerRow);
  }

  // `actors` fehlt bei alten Prüfungen ganz — kein Fehler, nur eine Prüfung ohne Schauspieler.
  for (const schauspieler of osce.actors ?? []) {
    if (schauspieler.id === undefined) continue;
    bundle.actors.push({
      id: kennung.actor(schauspieler.id),
      firstName: schauspieler.firstname ?? '',
      lastName: schauspieler.lastname ?? '',
      groupName: schauspieler.groupname ?? null,
    } satisfies ActorRow);
    bundle.examActors.push({
      id: kennung.examActor(osce.id, schauspieler.id),
      examID: examId,
      actorID: kennung.actor(schauspieler.id),
    } satisfies ExamActorRow);
  }

  for (const [id, eintrag] of prüflinge) {
    bundle.candidates.push(eintrag.zeile);
    bundle.examCandidates.push({
      id: kennung.examCandidate(osce.id, id),
      examID: examId,
      candidateID: eintrag.zeile.id,
      examCandidateNo: eintrag.nummer,
    } satisfies ExamCandidateRow);
  }

  baueBausteine(bundle, osce);

  return { bundle, medien };
}

/**
 * Der Orientierungstext einer global bewerteten Aufgabe: die Texte aller Antwortzeilen
 * untereinander, so wie der Classic-Client sie zeigt.
 *
 * Getrennt durch `\n` — jede Zeile ein eigener Absatz (`TaskCard` bricht daran um). Leere
 * Zeilen fallen weg, sonst entstünden Lücken im Block; ihre Kennung bleibt trotzdem erhalten
 * (s. unten), damit die Zählung der Wortindizes stimmt.
 */
function blockAusZeilen(antworten: ClassicAnswer[]): string {
  return antworten
    .map((a) => alsText(a.answer ?? ''))
    .filter((t) => t !== '')
    .join('\n');
}

function baueAufgabe(
  bundle: ExamBundle,
  aufgabe: ClassicTask,
  containerId: string,
  stationsName: string,
): void {
  if (aufgabe.id === undefined) {
    throw new ClassicPruefungUnlesbar('Eine Aufgabe hat keine Kennung.', { station: stationsName });
  }
  const itemId = kennung.item(aufgabe.id);
  const questionId = kennung.question(aufgabe.id);
  const beschreibung = aufgabe.description ?? '';
  // Der Name darf Auszeichnung tragen (`TaskCard` zeigt sie); die Kurzbezeichnung macht
  // `readExam` daraus Klartext.
  const volltext = alsAnzeigeHtml(beschreibung);
  const kurz = alsKurztext(beschreibung);
  const stelle = { station: stationsName, aufgabe: kurz || String(aufgabe.id) };

  const antworten = [...(aufgabe.answers ?? [])].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
  const arten = antworten.map((a) => bewertungsartVon(a, stelle));
  /**
   * **Alle** Zeilen global — dann ist die ganze Aufgabe global bewertet, gleich wie viele
   * Zeilen es sind. Eine einzelne globale Zeile **neben** einer Checkliste oder Skala ist
   * dagegen etwas anderes, s. den Wurf unten.
   */
  const global = antworten.length > 0 && arten.every((a) => a.art === 'global');

  if (!global && arten.some((a) => a.art === 'global')) {
    /*
     * ⚠️ **Hier liegt wirklich eine Mischung vor** — eine frei bewertete Zeile neben
     * angekreuzten oder skalierten. Relay kennt das nicht: entweder die Aufgabe trägt Zeilen
     * (Checkliste/Kriterien) oder sie trägt keine (global). Die freie Zeile liesse sich nur
     * darstellen, indem sie im Orientierungstext verschwindet — und mit ihr ihre Punkte.
     *
     * Das ist der Rest des alten Wurfs. Er galt bis zum 2026-09-13 auch für Aufgaben, deren
     * Zeilen **alle** global sind; die sind der Normalfall und werden oben zusammengesetzt.
     */
    const gemischt = [...new Set(arten.map((a) => a.art))].join(' + ');
    throw new ClassicPruefungUnlesbar(
      'Die Aufgabe mischt Bewertungsarten in einer Zeilenliste (' + gemischt + '). Relay kennt ' +
        'nur entweder Zeilen (Checkliste, Kriterien) oder keine (globale Punkte) — die frei ' +
        'bewertete Zeile liesse sich nur darstellen, indem ihre Punkte verschwinden.',
      stelle,
    );
  }

  /*
   * ⚠️ **„Global" beschreibt in Classic die Zeile, in Relay die Aufgabe.**
   *
   * Classics `ims.osce.global` sagt: diese Antwortzeile wird nicht angekreuzt. Eine Aufgabe
   * darf **mehrere** davon tragen — der Classic-Client zeigt ihre Texte dann als einen Block
   * untereinander, markierbar, und die Punkte vergibt der Prüfer einmal für die ganze Aufgabe
   * (am Gerät nachgesehen, Nutzer am 2026-09-13). Die Punkte der einzelnen Zeilen sind
   * Redaktionsangaben; deshalb ergibt ihre Summe bei fünf Aufgaben der Prüfung 98 nicht
   * einmal das Aufgabenmaximum.
   *
   * Relay drückt „global bewertet" dadurch aus, dass es **gar keine** `ExamAnswer` gibt
   * (`readExam.ts::scoringKindOf`), und der Text lebt in `questionText` weiter. Der Block wird
   * hier also zusammengesetzt — es geht nichts verloren.
   *
   * Bis zum 2026-09-13 stand hier ein Wurf gegen mehrere Zeilen. Er stammte aus den beiden
   * Prüfungen 70 und 73, wo jede Aufgabe genau eine, leere Zeile trug — die Stichprobe war
   * der Sonderfall, nicht die Regel.
   */

  const max = bruchAus(aufgabe.maxPoints, aufgabe.maxPointsNumerator, aufgabe.maxPointsDenominator);

  bundle.items.push({
    id: itemId,
    examItemContainerID: containerId,
    /**
     * ⚠️ **Ungekürzt.** `ExamItem.name` wird zu `Task.title` und ist im Classic-Bestand nicht
     * eine Überschrift, sondern **die Frage selbst** — am echten Server überschreiten 33 von
     * 360 Aufgabentexten 120 Zeichen (bis 305). Ein Schnitt dort verschwiege dem Prüfer den
     * zweiten Teil der Frage. Die kurze Fassung steht daneben in `anchorCriterion`, wohin sie
     * gehört: Kopfzeilen und Popups (`Task.short`).
     */
    name: volltext || String(aufgabe.id),
    type: 'TASK',
    orderNo: aufgabe.sortOrder ?? 0,
    maxPointsNumerator: max.n,
    maxPointsDenominator: max.d,
    anchorCriterion: kurz || null,
    // ⚠️ `pointsInterval` geht verloren — Relay hat kein Feld für die Schrittweite der
    // Punkteingabe (P5). Der Prüfer vergibt im Classic-Modus in unseren Schritten.
    comment: null,
  } satisfies ExamItemRow);

  bundle.questions.push({
    id: questionId,
    examItemID: itemId,
    /**
     * Bei globaler Bewertung liest `readExam` hier den Orientierungstext, dessen Wortgruppen
     * sich markieren lassen (`SELECTED_WORDS`). In Classic ist das der Text der **Antwortzeile**,
     * nicht der Aufgabentext — der steht oben im Namen.
     *
     * ⚠️ Er ist meistens leer: am echten Server tragen 338 von 412 Antworten nur ein
     * `<p>&#xa0;</p><p>&#8203;</p>`. Das ist kein Fehler und wird **nicht** mit dem
     * Aufgabentext aufgefüllt — auch das alte Gerät zeigt dort dann nichts, und die Frage
     * stünde sonst zweimal untereinander.
     */
    questionText: global ? blockAusZeilen(antworten) : null,
    // Der Aufgabentext selbst, ungekürzt und als HTML. Heute liest ihn niemand; er steht hier,
    // damit der Download nichts wegwirft, was Etappe 2 (Anzeige, P15) noch braucht.
    vignette: beschreibung || null,
  } satisfies ExamQuestionRow);

  if (global) {
    /**
     * ⚠️ **Hier geht die einzige Kennung verloren, die der Download nicht mitträgt.**
     *
     * Relay drückt „global bewertet" dadurch aus, dass es gar keine `ExamAnswer` gibt
     * (`readExam.ts::scoringKindOf`) — die Antwortzeile verschwindet also, ihr Text lebt in
     * `questionText` weiter. Für die **Punkte** ist das folgenlos: sie gehen beim
     * Zurückschreiben ins Aufgabenbewertung, wie beim alten Gerät auch.
     *
     * Die **Wortmarkierung** hängt in Classic aber am `Zeilenbewertung` genau dieser Zeile, auch im
     * globalen Fall. Ohne ihre Kennung liesse sich `SELECTED_WORDS` nicht zurückschreiben.
     * Deshalb wird sie hier gemerkt, statt sie stillschweigend zu verlieren.
     */
    /*
     * ⚠️ **Eine Zeile ohne Kennung wird mitgezählt, nicht weggelassen.** Sie steht trotzdem im
     * Block und liefert Wörter; liesse man sie hier aus, verschöbe sich der Beginn **jeder**
     * folgenden Zeile um ihre Wortzahl, und die Marken sässen am Classic-Server auf falschen
     * Wörtern. Sie bekommt deshalb die Kennung `0`; `verteileWortindexe` rückt an ihr weiter,
     * ohne ein `Zeilenbewertung` für sie zu bauen (es gäbe keine `answerID` dafür).
     */
    const zeilen: GlobaleAntwortzeile[] = antworten.map((a) => ({
      id: a.id ?? 0,
      woerter: woerterIn(alsText(a.answer ?? '')),
    }));
    if (zeilen.length > 0) {
      bundle.classicKennungen = bundle.classicKennungen ?? [];
      bundle.classicKennungen.push({
        id: kennungGlobalerAntwort(itemId),
        examID: bundle.exam.id,
        roh: zeilenAlsRoh(zeilen),
      } satisfies ClassicKennungRow);
    }
    return;
  }

  antworten.forEach((antwort, i) => {
    if (antwort.id === undefined) {
      throw new ClassicPruefungUnlesbar('Eine Antwortzeile hat keine Kennung.', stelle);
    }
    const art = arten[i];
    const punkte = bruchAus(antwort.maxPoints, antwort.maxPointsNumerator, antwort.maxPointsDenominator);
    bundle.answers.push({
      id: kennung.answer(antwort.id),
      examQuestionID: questionId,
      answerText: alsAnzeigeHtml(antwort.answer ?? ''),
      orderNo: antwort.sortOrder ?? i,
      // Die Stufenbeschriftungen stehen in der Prüfung selbst (`Stufe1#Stufe2#…`) — hier wird
      // nichts erfunden. Ohne Stufen ist es eine Checkliste, und `readExam` liest es so.
      scalaValues: art?.art === 'kriterien' ? JSON.stringify(art.stufen) : null,
      maxPointsNumerator: punkte.n,
      maxPointsDenominator: punkte.d,
      comment: null,
    } satisfies ExamAnswerRow);
  });
}

/**
 * Bausteine: Classic führt **einen Baum je Prüfung**, Relay eine Liste, die an der einzelnen
 * Aufgabe hängt. Also eine erfundene Liste je Prüfung, verknüpft mit jeder Aufgabe (P7).
 *
 * Die Einträge werden von der Wurzel nach unten sortiert ausgegeben:
 * `parentPredefinedCommentPhraseID` zeigt auf dieselbe Tabelle, und ein Kind vor seinem
 * Elternteil verletzt den Fremdschlüssel. Im Browser fiele das nicht auf, auf dem Gerät schon.
 */
function baueBausteine(bundle: ExamBundle, osce: ClassicOsce): void {
  const roh = (osce.phrases ?? []).filter((p): p is ClassicPhrase & { id: number } => p.id !== undefined);
  if (roh.length === 0) return;

  const listId = kennung.phraseList(osce.id as number);
  bundle.phraseLists?.push({ id: listId, name: osce.name ?? String(osce.id) });

  const vorhanden = new Set(roh.map((p) => p.id));
  const elternVon = (p: ClassicPhrase): number | null => {
    const eltern = p.parentphraseID;
    // ⚠️ Classic führt die Wurzel als Zahl, nicht als `null` — `0` und `-1` bedeuten beide
    // „kein Elternteil". Ein Verweis auf eine Phrase, die nicht mitgeliefert wurde, zählt
    // ebenfalls als Wurzel; sonst stünde der Eintrag nirgends.
    if (typeof eltern !== 'number' || eltern <= 0 || !vorhanden.has(eltern)) return null;
    return eltern;
  };

  const offen = [...roh];
  const fertig = new Set<number>();
  const sortiert: PredefinedCommentPhraseRow[] = [];

  while (offen.length > 0) {
    const vorher = offen.length;
    for (let i = 0; i < offen.length; i += 1) {
      const p = offen[i];
      const eltern = elternVon(p);
      if (eltern !== null && !fertig.has(eltern)) continue;
      sortiert.push({
        id: kennung.phrase(p.id),
        predefinedCommentPhraseListID: listId,
        parentPredefinedCommentPhraseID: eltern === null ? null : kennung.phrase(eltern),
        name: p.name ?? '',
        fullText: p.fulltext ?? '',
        orderNo: p.reihenfolge ?? 0,
      });
      fertig.add(p.id);
      offen.splice(i, 1);
      i -= 1;
    }
    if (offen.length === vorher) {
      // Ein Kreis in den Elternverweisen. Nicht schweigend abschneiden: der Rest kommt als
      // Wurzel heraus, und die Schleife endet.
      for (const p of offen) {
        sortiert.push({
          id: kennung.phrase(p.id),
          predefinedCommentPhraseListID: listId,
          parentPredefinedCommentPhraseID: null,
          name: p.name ?? '',
          fullText: p.fulltext ?? '',
          orderNo: p.reihenfolge ?? 0,
        });
      }
      break;
    }
  }

  bundle.phrases?.push(...sortiert);
  for (const item of bundle.items) {
    bundle.itemPhraseLists?.push({
      id: 'classic:itemphraselist:' + item.id + ':' + listId,
      examItemID: item.id,
      predefinedCommentPhraseListID: listId,
    } satisfies ExamItemPredefinedCommentPhraseListRow);
  }
}
